import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '@/app/baseQuery';
import Jsona from 'jsona';
import decamelizeKeysDeep from 'decamelize-keys-deep';
import type { ProfileList } from './types/list-types';

const jsona = new Jsona();

/**
 * Helper function to serialize data for POST/PATCH/DELETE requests
 */
function serializeListData(
  queryParams: Record<string, any>,
  type: string = 'profile-list'
) {
  return decamelizeKeysDeep(
    jsona.serialize({
      stuff: { ...queryParams, type },
      includeNames: queryParams.relationshipNames || []
    }),
    '-'
  );
}

interface CreateCustomProfileListParams {
  profileIds: string[];
  listName: string;
}

interface RemoveCustomListParams {
  listId: string;
}

interface UpdateCustomListParams {
  list: Pick<ProfileList, 'id' | 'name'>;
  profileIds: string[];
}

interface HideProfileFromFeedParams {
  profileId: string;
}

interface BanProfileParams {
  profileId: string;
}

interface RemoveProfileFromListParams {
  profileId: string;
  listId: string;
}

export const listApi = createApi({
  reducerPath: 'listApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Lists'],
  endpoints: builder => ({
    /**
     * GET /lists - Get all lists with profile details
     */
    getLists: builder.query<ProfileList[], void>({
      query: () => ({
        url: 'lists',
        params: { include: 'profile' }
      }),
      providesTags: ['Lists']
    }),

    /**
     * GET /lists - Get lists without profile details
     */
    getListsWithoutProfiles: builder.query<ProfileList[], void>({
      query: () => ({
        url: 'lists'
      }),
      providesTags: ['Lists']
    }),

    /**
     * GET /lists with action=custom&scope=blog - Get custom lists
     */
    getCustomLists: builder.query<ProfileList[], void>({
      query: () => ({
        url: 'lists',
        params: {
          action: 'custom',
          scope: 'blog',
          include: 'profile'
        }
      }),
      providesTags: ['Lists']
    }),

    /**
     * POST /lists - Create a custom profile list
     */
    createCustomProfileList: builder.mutation<
      ProfileList[],
      CreateCustomProfileListParams
    >({
      query: ({ profileIds, listName }) => {
        const queryParams = {
          action: 'custom',
          scope: 'blog',
          name: listName,
          kind: 'profile',
          profiles: profileIds.map(id => ({ id, type: 'profiles' as const })),
          relationshipNames: ['profiles']
        };

        const nonce = new Date().getTime();

        return {
          url: `lists?nonce=${nonce}`,
          method: 'POST',
          body: serializeListData(queryParams)
        };
      },
      invalidatesTags: ['Lists']
    }),

    /**
     * DELETE /lists/{listId} - Remove a custom list
     */
    removeCustomList: builder.mutation<void, RemoveCustomListParams>({
      query: ({ listId }) => ({
        url: `lists/${listId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Lists']
    }),

    /**
     * PATCH /lists/{listId} - Update a custom list
     */
    updateCustomList: builder.mutation<ProfileList[], UpdateCustomListParams>({
      query: ({ list, profileIds }) => {
        const queryParams = {
          action: 'custom',
          scope: 'blog',
          name: list.name,
          profiles: profileIds.map(id => ({ id, type: 'profiles' as const })),
          relationshipNames: ['profiles']
        };

        return {
          url: `lists/${list.id}`,
          method: 'PATCH',
          body: serializeListData(queryParams, 'profile-list')
        };
      },
      invalidatesTags: ['Lists']
    }),

    /**
     * POST /lists - Hide a profile from feed
     */
    hideProfileFromFeed: builder.mutation<
      ProfileList[],
      HideProfileFromFeedParams
    >({
      query: ({ profileId }) => {
        const queryParams = {
          action: 'hide',
          scope: 'feed',
          kind: 'profile',
          profiles: [{ id: profileId, type: 'profiles' as const }],
          relationshipNames: ['profiles']
        };

        const nonce = new Date().getTime();

        return {
          url: `lists?nonce=${nonce}`,
          method: 'POST',
          body: serializeListData(queryParams)
        };
      },
      invalidatesTags: ['Lists']
    }),

    /**
     * POST /lists - Ban a profile from blog
     */
    banProfile: builder.mutation<ProfileList[], BanProfileParams>({
      query: ({ profileId }) => {
        const queryParams = {
          action: 'ban',
          kind: 'profile',
          scope: 'blog',
          profiles: [{ id: profileId, type: 'profiles' as const }],
          relationshipNames: ['profiles']
        };

        const nonce = new Date().getTime();

        return {
          url: `lists?nonce=${nonce}`,
          method: 'POST',
          body: serializeListData(queryParams)
        };
      },
      invalidatesTags: ['Lists']
    }),

    /**
     * DELETE /lists/{listId}/relationships/profiles - Remove a profile from a list
     */
    removeProfileFromList: builder.mutation<void, RemoveProfileFromListParams>({
      query: ({ profileId, listId }) => {
        const queryParams = {
          id: profileId,
          relationshipNames: []
        };

        return {
          url: `lists/${listId}/relationships/profiles`,
          method: 'DELETE',
          body: serializeListData(queryParams)
        };
      },
      invalidatesTags: ['Lists']
    })
  })
});

export const {
  useGetListsQuery,
  useGetListsWithoutProfilesQuery,
  useGetCustomListsQuery,
  useCreateCustomProfileListMutation,
  useRemoveCustomListMutation,
  useUpdateCustomListMutation,
  useHideProfileFromFeedMutation,
  useBanProfileMutation,
  useRemoveProfileFromListMutation
} = listApi;
